import { useEffect, useRef, useState } from "react";
import { useInView, useSpring } from "framer-motion";

// Motion is local to the $10M card. No pointer listeners or render loop on the page.
export const useLegendaryMotion = () => {
  const ref = useRef(null);
  const beamRef = useRef(null);
  const bounds = useRef(null);
  const timer = useRef(null);
  const inView = useInView(ref, { margin: "60px" });
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [hovering, setHovering] = useState(false);
  const [burst, setBurst] = useState(false);
  const rotateX = useSpring(0, { stiffness: 150, damping: 24 });
  const rotateY = useSpring(0, { stiffness: 150, damping: 24 });

  useEffect(() => {
    // Subscribe directly: this installed Motion version snapshots the preference on mount.
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onPreference = () => setReduced(preference.matches);
    const onVisibility = () => setVisible(!document.hidden);
    preference.addEventListener("change", onPreference);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      preference.removeEventListener("change", onPreference);
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    // Playback rate preserves the beam's position; changing duration would make it jump.
    beamRef.current?.getAnimations({ subtree: true }).forEach((animation) => {
      animation.updatePlaybackRate(burst && !reduced ? 2.2 : 1);
    });
  }, [burst, reduced, inView]);

  useEffect(() => {
    if (reduced || !inView || !visible) {
      rotateX.jump(0);
      rotateY.jump(0);
      setHovering(false);
      setBurst(false);
      clearTimeout(timer.current);
    }
  }, [reduced, inView, visible, rotateX, rotateY]);

  const onPointerEnter = (event) => {
    if (event.pointerType !== "mouse" || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    bounds.current = ref.current.getBoundingClientRect();
    setHovering(true);
    if (reduced) return;
    setBurst(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setBurst(false), 1000);
  };
  const onPointerMove = (event) => {
    if (reduced || event.pointerType !== "mouse" || !bounds.current || !hovering) return;
    const { left, top, width, height } = bounds.current;
    const x = Math.max(-0.5, Math.min(0.5, (event.clientX - left) / width - 0.5));
    const y = Math.max(-0.5, Math.min(0.5, (event.clientY - top) / height - 0.5));
    rotateX.set(-y * 5);
    rotateY.set(x * 5);
  };
  const onPointerLeave = () => {
    bounds.current = null;
    setHovering(false);
    setBurst(false);
    clearTimeout(timer.current);
    rotateX.set(0);
    rotateY.set(0);
  };

  return {
    ref, beamRef, hovering, burst, reduced,
    running: inView && visible && !reduced,
    style: { rotateX: reduced ? 0 : rotateX, rotateY: reduced ? 0 : rotateY, transformPerspective: 1100 },
    onPointerEnter, onPointerMove, onPointerLeave,
  };
};